import { ScoutDataChangeStatus } from "generated-graphql/graphql";

import type { ScoutDataChangeSummary } from "../hooks/types";

export type ScoutDataChangeUndoResult = {
  changeId: string;
  status?: string | null;
  failureCategory?: string | null;
  failureReason?: string | null;
};

/** Card chip applied immediately from the undo mutation, before the stream patch. */
export type UndoTerminalCardStatus = "undone" | "undo_failed";

/** Business-level undo failure. The GraphQL mutation itself usually succeeds. */
export class ScoutDataChangeUndoError extends Error {
  readonly failureCategory: string | null;
  readonly failureReason: string | null;

  constructor(input: { failureCategory?: string | null; failureReason?: string | null }) {
    super(input.failureReason ?? "Undo did not go through.");
    this.name = "ScoutDataChangeUndoError";
    this.failureCategory = input.failureCategory ?? null;
    this.failureReason = input.failureReason ?? null;
  }
}

export const UNDO_FAILURE_MESSAGES = {
  STALE: "Couldn't undo this change because the data was updated after Scout applied it.",
  WINDOW_PASSED: "Couldn't undo this change because the undo window has expired.",
  NOT_UNDOABLE: "This change can't be undone.",
  REVERSE_FAILED: "Couldn't undo this change. Ask Encamp support if it still needs reversing.",
} satisfies Record<string, string>;

export const UNDO_FAILURE_MESSAGES_PLURAL = {
  STALE: "Couldn't undo these changes because the data was updated after Scout applied them.",
  WINDOW_PASSED: "Couldn't undo these changes because the undo window has expired.",
  NOT_UNDOABLE: "These changes can't be undone.",
  REVERSE_FAILED: "Couldn't undo these changes. Ask Encamp support if they still need reversing.",
} satisfies Record<string, string>;

export const UNDO_FAILURE_STALE_MESSAGE = UNDO_FAILURE_MESSAGES.STALE;
export const UNDO_FAILURE_STALE_MESSAGE_PLURAL = UNDO_FAILURE_MESSAGES_PLURAL.STALE;

export const UNDO_FAILURE_GENERIC_MESSAGE =
  "Something went wrong undoing this change. Please try again.";

export const UNDO_FAILURE_GENERIC_MESSAGE_PLURAL =
  "Something went wrong undoing these changes. Please try again.";

export type UndoFailureDisplay = {
  message: string;
  staffReason?: string;
  isStale: boolean;
};

export function isStaleUndoFailureCategory(category: string | null | undefined): boolean {
  return category?.toUpperCase() === "STALE";
}

type UndoFailureCategory = keyof typeof UNDO_FAILURE_MESSAGES;

function isUndoFailureCategory(category: string): category is UndoFailureCategory {
  return Object.hasOwn(UNDO_FAILURE_MESSAGES, category);
}

function undoFailureMessageByCategory(
  category: string | undefined,
  changeCount: number
): string | undefined {
  if (!category || !isUndoFailureCategory(category)) return undefined;
  const messages = changeCount > 1 ? UNDO_FAILURE_MESSAGES_PLURAL : UNDO_FAILURE_MESSAGES;
  return messages[category];
}

export function formatUndoFailureDisplay(
  error: unknown,
  options?: { changeCount?: number }
): UndoFailureDisplay | undefined {
  if (error == null) return undefined;
  const changeCount = options?.changeCount ?? 1;
  const generic =
    changeCount > 1 ? UNDO_FAILURE_GENERIC_MESSAGE_PLURAL : UNDO_FAILURE_GENERIC_MESSAGE;
  if (!(error instanceof ScoutDataChangeUndoError)) {
    return { message: generic, isStale: false };
  }
  const category = error.failureCategory?.toUpperCase();
  const isStale = isStaleUndoFailureCategory(category);
  const categoryMessage = undoFailureMessageByCategory(category, changeCount);
  const message = categoryMessage ?? generic;
  const staffReason = error.failureReason?.trim();
  if (staffReason && staffReason.length > 0 && staffReason !== message) {
    return { message, staffReason, isStale };
  }
  return { message, isStale };
}

function uniqueStaffReasons(
  failures: ReadonlyArray<ScoutDataChangeUndoResult>
): string | undefined {
  const reasons: string[] = [];
  for (const failure of failures) {
    const reason = failure.failureReason?.trim();
    if (!reason || reasons.includes(reason)) continue;
    reasons.push(reason);
  }
  if (reasons.length === 0) return undefined;
  return reasons.join("\n");
}

/** One customer-facing error for a batch undo that failed on one or more ids. */
export function formatBatchUndoFailureDisplay(
  failures: ReadonlyArray<ScoutDataChangeUndoResult>
): UndoFailureDisplay | undefined {
  if (failures.length === 0) return undefined;
  const first = failures[0];
  if (failures.length === 1 && first) {
    return formatUndoFailureDisplay(new ScoutDataChangeUndoError(first));
  }
  const categories = new Set<string>();
  let isStale = false;
  for (const failure of failures) {
    const category = failure.failureCategory?.toUpperCase();
    if (category) categories.add(category);
    if (isStaleUndoFailureCategory(category)) isStale = true;
  }
  let message = UNDO_FAILURE_GENERIC_MESSAGE_PLURAL;
  if (categories.size === 1) {
    const category = [...categories][0];
    const categoryMessage = undoFailureMessageByCategory(category, failures.length);
    if (categoryMessage) message = categoryMessage;
  }
  const staffReason = uniqueStaffReasons(failures);
  if (staffReason && staffReason !== message) {
    return { message, staffReason, isStale };
  }
  return { message, isStale };
}

/**
 * A failed undo as history reloads it. Undos that failed before the category
 * was stored fall back to the ask-support copy.
 */
export function persistedUndoFailure(
  summary: ScoutDataChangeSummary | undefined
): ScoutDataChangeUndoResult | undefined {
  if (summary?.status !== ScoutDataChangeStatus.UndoFailed) return undefined;
  return {
    changeId: summary.id,
    status: summary.status,
    failureCategory: summary.undoFailureCategory ?? "REVERSE_FAILED",
    failureReason: summary.failureReason ?? null,
  };
}

export function staleUndoFailureChangeIds(
  failures: ReadonlyArray<ScoutDataChangeUndoResult>
): string[] {
  const ids: string[] = [];
  for (const failure of failures) {
    if (!isStaleUndoFailureCategory(failure.failureCategory)) continue;
    if (failure.changeId.length === 0 || ids.includes(failure.changeId)) continue;
    ids.push(failure.changeId);
  }
  return ids;
}

export function formatUndoFailureMessage(error: unknown, options?: { isStaff?: boolean }): string {
  const display = formatUndoFailureDisplay(error);
  if (!display) return UNDO_FAILURE_GENERIC_MESSAGE;
  if (options?.isStaff && display.staffReason) {
    return `${display.message}\n${display.staffReason}`;
  }
  return display.message;
}

/** Undo failed when the result carries a failure category and/or reason. */
export function isUndoResultFailure(result: {
  failureCategory?: string | null;
  failureReason?: string | null;
}): boolean {
  return Boolean(result.failureCategory) || Boolean(result.failureReason);
}

/**
 * Status chip to show as soon as the mutation returns, so Undo / Applied do
 * not linger until `data_change_undo_updated` arrives.
 */
export function terminalCardStatusForUndoResult(
  result: ScoutDataChangeUndoResult
): UndoTerminalCardStatus | undefined {
  const status = result.status?.toUpperCase();
  if (status === "UNDONE") return "undone";
  if (status === "UNDO_FAILED") return "undo_failed";
  if (isUndoResultFailure(result)) return "undo_failed";
  return undefined;
}

export function findUndoResultFailure(
  results: ReadonlyArray<ScoutDataChangeUndoResult> | void,
  changeId: string
): ScoutDataChangeUndoResult | undefined {
  if (!results) return undefined;
  const result = results.find((row) => row.changeId === changeId);
  if (!result || !isUndoResultFailure(result)) return undefined;
  return result;
}

export function collectUndoResultFailures(
  results: ReadonlyArray<ScoutDataChangeUndoResult> | void,
  changeIds: string[]
): ScoutDataChangeUndoResult[] {
  const failures: ScoutDataChangeUndoResult[] = [];
  for (const changeId of changeIds) {
    const failure = findUndoResultFailure(results, changeId);
    if (failure) failures.push(failure);
  }
  return failures;
}
